import { clamp } from "three/src/math/MathUtils.js";
import type { CartesianPosition, MaterialPreset, PolarPosition } from "./types";
import { Color, type MeshStandardMaterialProperties } from "three";

const EPS = 0.01;

/**
 * Converts orbit angles (latitude, longitude, distance)
 * into a camera position in a Z-up coordinate system.
 */
export function cameraOrbitToCartesian(
  polar: PolarPosition
): CartesianPosition {
  const latitude = clamp(polar.latitude, -Math.PI / 2 + EPS, Math.PI / 2 - EPS);
  const longitude = clamp(polar.longitude, -Math.PI + EPS, Math.PI - EPS);
  return {
    x: polar.distance * Math.cos(latitude) * Math.sin(longitude),
    y: -polar.distance * Math.cos(longitude) * Math.cos(latitude),
    z: polar.distance * Math.sin(latitude),
  };
}

export const MATERIAL_PRESETS: Record<
  MaterialPreset,
  Partial<MeshStandardMaterialProperties>
> = {
  // Plastics
  ACETAL: {
    color: new Color("#f5f5f5"),
    roughness: 0.6,
    metalness: 0.0,
  },

  ACRYLIC: {
    color: new Color("#ffffff"),
    roughness: 0.3,
    metalness: 0.0,
  },

  TEMPORARY_PMMA: {
    color: new Color("#fdfdf7"),
    roughness: 0.4,
    metalness: 0.0,
  },

  // 🦷 Ceramics
  ZIRCONIA: {
    color: new Color("#f8f8f6"),
    roughness: 0.35,
    metalness: 0.0,
  },

  ZIRCONIA_FULL: {
    color: new Color("#ffffff"),
    roughness: 0.3,
    metalness: 0.0,
  },

  ZIRCONIA_MULTILAYER: {
    color: new Color("#f4f4f2"),
    roughness: 0.25,
    metalness: 0.0,
  },

  PORCELAIN_FUSED_ZIRCONIA: {
    color: new Color("#faf9f6"),
    roughness: 0.3,
    metalness: 0.0,
  },

  EMAX_FULL: {
    color: new Color("#fefaf2"),
    roughness: 0.25,
    metalness: 0.0,
  },

  EMAX_MULTILAYER: {
    color: new Color("#fdf8ee"),
    roughness: 0.2,
    metalness: 0.0,
  },

  COMPOSITE_NO_METAL: {
    color: new Color("#f7f5f0"),
    roughness: 0.5,
    metalness: 0.0,
  },

  // Metals
  TITANIUM: {
    color: new Color("#b8bcc2"),
    roughness: 0.35,
    metalness: 1.0,
  },

  TITANIUM_FRAME: {
    color: new Color("#aeb3b9"),
    roughness: 0.4,
    metalness: 1.0,
  },

  TI_BASE: {
    color: new Color("#c0c4c9"),
    roughness: 0.3,
    metalness: 1.0,
  },

  COBALT_CHROME_METALLIC_FRAME: {
    color: new Color("#9ea4aa"),
    roughness: 0.25,
    metalness: 1.0,
  },

  METAL_NON_PRECIOUS: {
    color: new Color("#9c9c9c"),
    roughness: 0.3,
    metalness: 1.0,
  },

  PFM_NON_PRECIOUS: {
    color: new Color("#e8e8e8"),
    roughness: 0.35,
    metalness: 0.5,
  },

  PFM_NON_PRECIOUS_POST_AND_CORE: {
    color: new Color("#dcdcdc"),
    roughness: 0.4,
    metalness: 0.7,
  },

  PFM_PRECIOUS: {
    color: new Color("#d4af37"),
    roughness: 0.2,
    metalness: 1.0,
  },

  // 🛡 Guards / Flexible materials
  NIGHT_GUARD_HARD_MATERIAL: {
    color: new Color("#ffffff"),
    roughness: 0.3,
    metalness: 0.0,
    opacity: 0.8,
    transparent: true,
  },

  NIGHT_GUARD_MEDIUM_MATERIAL: {
    color: new Color("#ffffff"),
    roughness: 0.4,
    metalness: 0.0,
    opacity: 0.7,
    transparent: true,
  },

  NIGHT_GUARD_SOFT_MATERIAL: {
    color: new Color("#ffffff"),
    roughness: 0.6,
    metalness: 0.0,
    opacity: 0.6,
    transparent: true,
  },

  CLEAR_RETAINER: {
    color: new Color("#ffffff"),
    roughness: 0.2,
    metalness: 0.0,
    opacity: 0.5,
    transparent: true,
  },

  RETAINER: {
    color: new Color("#f8f8f8"),
    roughness: 0.4,
    metalness: 0.0,
  },

  BLEACHING_TRAY: {
    color: new Color("#ffffff"),
    roughness: 0.2,
    metalness: 0.0,
    opacity: 0.4,
    transparent: true,
  },

  VALPLAST: {
    color: new Color("#ffb3b3"),
    roughness: 0.6,
    metalness: 0.0,
  },

  // Wax
  WAX_RIM: {
    color: new Color("#ff6f61"),
    roughness: 0.8,
    metalness: 0.0,
  },

  FULL_DENTURE_WAX_BITE: {
    color: new Color("#ff7f66"),
    roughness: 0.85,
    metalness: 0.0,
  },
};
